import { TripTimelineEvent } from "trips/models";
import apiClient from "./apiClient";
import { toast } from "react-toastify";

const mergeEvents = async (id, events): Promise<any> => {
  var request = {
    tripId: id,
    events: events.map((e) => ({
      eventId: e.eventId,
      eventTypeId: e.typeId,
      comments: e.comments,
      time: e.time,
      odometer: e.odometer,
      routeLocation: e.routeLocation || null,
      closingComments: e.closingComments,
      isActive: e.isActive,
      isSuggested: e.isSuggested || false,
      suggestedEventId: e.suggestedEventId || null,
      suggestionContext: e.suggestionContext || null,
    })),
  };
  return apiClient
    .post(`/events/merge`, request)
    .then((t) => toast.success("Events Updated."))
    .catch((e) => toast.error("Events Update Failed"));
};

const validate = async (tripId, events): Promise<any> => {
  const request = {
    tripId: tripId,
    events: events.map((e) => ({
      eventId: e.eventId,
      eventTypeId: e.typeId,
      comments: e.comments,
      time: e.time,
      odometer: e.odometer,
      routeLocation: e.routeLocation,
      closingComments: e.closingComments,
      isActive: e.isActive,
    })),
  };
  return apiClient.post(`/events/validate`, request);
};

// Mirrors Flow.Services.Models.EventResetResult. blockedReason is set when the reset declined
// to run (open trip, no GPS, missing Begin/End anchor) - nothing was changed in that case.
export interface EventResetResult {
  tripId: number;
  deactivatedEventCount: number;
  removedSuggestionCount: number;
  regeneratedSuggestionCount: number;
  blockedReason?: string | null;
  wasReset: boolean;
}

const getTimeline = async (tripNumber: string): Promise<TripTimelineEvent[]> => {
  return apiClient.get(`/events/timeline/${tripNumber}`);
};

// Event Reset: throw away everything between Begin Trip and End Trip and rebuild the middle
// from GPS. Admin-only and destructive - the caller confirms before calling this.
const resetTripEvents = async (tripId: number): Promise<EventResetResult> => {
  return apiClient.post(`/events/trip/${tripId}/reset`, {});
};

// Mirrors Flow.Services.Models.BulkEventResetResult. reset is null when the trip failed before
// the reset itself ran; message says why.
export interface BulkEventResetResult {
  tripId: number;
  succeeded: boolean;
  message?: string | null;
  reset?: EventResetResult | null;
  acceptedSuggestionCount: number;
  // The submission (OrderApi dbo.EventResetBatch) this trip ran in; sent back on later chunks.
  batchId?: string | null;
  // Failed an eligibility rule and was left untouched; message says which.
  skipped?: boolean;
  autoApproveRan?: boolean;
  autoApproved?: boolean;
  autoApproveFailReason?: string | null;
  autoApproveError?: string | null;
}

// Bulk Event Reset from the review queue: the same reset per trip, then the rebuilt suggestions
// are accepted and the trip is run through the auto-approver. Destructive - the caller confirms
// before calling this. All chunks of one selection share a batch: the first chunk omits batchId,
// later chunks send back the batchId the first returned, and the last sets isFinalChunk.
const bulkResetTripEvents = async (
  tripIds: number[],
  batchId?: string | null,
  isFinalChunk?: boolean
): Promise<BulkEventResetResult[]> => {
  return apiClient.post(`/events/bulk-reset`, { tripIds, batchId: batchId ?? null, isFinalChunk: isFinalChunk ?? true });
};

const getTripEventsExport = async (id: number, timezone: string): Promise<any> => {
  return apiClient.getBlob(`/events/export/${id}?timezone=${timezone}`);
};

const saveAndValidate = async (id, events): Promise<any> => {
  const request = {
    tripId: id,
    events: events.map((e) => ({
      eventId: e.eventId,
      eventTypeId: e.typeId,
      comments: e.comments,
      time: e.time,
      odometer: e.odometer,
      routeLocation: e.routeLocation || null,
      closingComments: e.closingComments,
      isActive: e.isActive,
      isSuggested: e.isSuggested || false,
      suggestedEventId: e.suggestedEventId || null,
      suggestionContext: e.suggestionContext || null,
    })),
  };

  return apiClient.post(`/events/merge`, request)
};

const setEventActive = async (eventId: number, isActive: boolean): Promise<any> => {
  return apiClient.put(`/events/${eventId}/active?isActive=${isActive}`);
};

const EventsService = {
  mergeEvents,
  validate,
  saveAndValidate,
  getTimeline,
  getTripEventsExport,
  setEventActive,
  resetTripEvents,
  bulkResetTripEvents,
};

export default EventsService;
